import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_LOCAL } from '../config/env';
import { fetchServerRelease, getLocalRelease } from '../services/pwa/release';

/**
 * Detects that a newer frontend has been deployed while this tab stays open.
 *
 * Signal: the content-derived release id stamped into the bundle at build
 * time (<meta name="prospero-release">) vs the one served by /build.json from
 * the SAME nginx that serves the bundle. Not the package.json version (manual
 * bump, backend-owned) and not the git SHA (changes on backend-only commits).
 *
 * A mismatch must be observed on TWO consecutive checks for the same server
 * release before the banner shows: during a rolling deploy the load balancer
 * can alternate old/new containers and a single observation proves nothing.
 *
 * The anti-loop guard is release-aware: after tapping "Aggiorna" the banner is
 * suppressed only for THAT release and only for a bounded window — polling
 * itself never stops, so the next deploy is always detected.
 */

export const UPDATE_ATTEMPT_KEY = 'prospero_update_attempt';
const LEGACY_KEYS = ['prospero_update_attempted', 'prospero_update_reload_started'];
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_CHECK_DELAY_MS = 3_000;
const CONFIRM_DELAY_MS = 10_000;
export const ATTEMPT_SUPPRESS_WINDOW_MS = 10 * 60 * 1000;

interface UpdateAttempt {
  release: string;
  ts: number;
}

function readAttempt(): UpdateAttempt | null {
  try {
    const raw = sessionStorage.getItem(UPDATE_ATTEMPT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as UpdateAttempt;
    return typeof parsed.release === 'string' && typeof parsed.ts === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

function writeAttempt(release: string): void {
  try {
    sessionStorage.setItem(UPDATE_ATTEMPT_KEY, JSON.stringify({ release, ts: Date.now() }));
  } catch {
    // sessionStorage may be unavailable (Safari private mode) — degrade to no guard.
  }
}

function isSuppressed(serverRelease: string): boolean {
  const attempt = readAttempt();
  return (
    attempt !== null &&
    attempt.release === serverRelease &&
    Date.now() - attempt.ts < ATTEMPT_SUPPRESS_WINDOW_MS
  );
}

function removeLegacyKeys(): void {
  try {
    for (const key of LEGACY_KEYS) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // best-effort hygiene
  }
}

async function clearCachesAndUnregister(): Promise<void> {
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.unregister();
    }
  }
}

export function useVersionCheck(opts?: { initialDelayMs?: number; confirmDelayMs?: number }): {
  updateRequired: boolean;
  dismissUpdate: () => void;
  clearAndReload: () => Promise<void>;
} {
  const initialDelay = opts?.initialDelayMs ?? INITIAL_CHECK_DELAY_MS;
  const confirmDelay = opts?.confirmDelayMs ?? CONFIRM_DELAY_MS;
  const [updateRequired, setUpdateRequired] = useState(false);
  const pendingMismatchRef = useRef<string | null>(null);
  const targetReleaseRef = useRef<string | null>(null);

  const dismissUpdate = useCallback(() => {
    setUpdateRequired(false);
  }, []);

  const clearAndReload = useCallback(async (): Promise<void> => {
    const target = targetReleaseRef.current;
    if (target) {
      writeAttempt(target);
    }
    await clearCachesAndUnregister().catch(() => undefined);
    window.location.reload();
  }, []);

  useEffect(() => {
    removeLegacyKeys();

    if (IS_LOCAL) {
      return;
    }
    const localRelease = getLocalRelease();
    if (!localRelease) {
      return;
    }

    let unmounted = false;
    let confirmTimeout: ReturnType<typeof setTimeout> | undefined;

    const checkVersion = async (): Promise<void> => {
      const serverRelease = await fetchServerRelease();
      if (unmounted || !serverRelease) {
        return;
      }

      if (serverRelease === localRelease) {
        pendingMismatchRef.current = null;
        return;
      }

      targetReleaseRef.current = serverRelease;
      if (isSuppressed(serverRelease)) {
        return;
      }

      if (pendingMismatchRef.current === serverRelease) {
        setUpdateRequired(true);
        return;
      }
      pendingMismatchRef.current = serverRelease;
      confirmTimeout = setTimeout(() => void checkVersion(), confirmDelay);
    };

    const initialTimeout = setTimeout(() => void checkVersion(), initialDelay);
    const interval = setInterval(() => void checkVersion(), VERSION_CHECK_INTERVAL_MS);

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      unmounted = true;
      clearTimeout(initialTimeout);
      clearTimeout(confirmTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [initialDelay, confirmDelay]);

  return { updateRequired, dismissUpdate, clearAndReload };
}
